import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, Copy, RotateCw, Sparkles, ScanText, ChevronDown, ChevronUp, Share2, Globe, Lock, Volume2, Target } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import ScoreRing from "../components/ScoreRing";
import AudioPlayer from "../components/AudioPlayer";
import { compareGoal } from "../components/GoalDialog";
import { Switch } from "../components/ui/switch";

const subscoreColor = (n) => (n >= 80 ? "bg-sage" : n >= 60 ? "bg-terracotta" : "bg-amber-500");

const goalTone = (state) => {
    if (state === "good") return "text-sage";
    if (state === "near") return "text-amber-600";
    return "text-terracotta";
};
const goalDot = (state) => {
    if (state === "good") return "bg-sage";
    if (state === "near") return "bg-amber-500";
    return "bg-terracotta";
};
const goalLabel = (state) => (state === "good" ? "On target" : state === "near" ? "Close" : "Off");

export default function Results() {
    const { sessionId } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [goal, setGoal] = useState(null);
    const [showTranscript, setShowTranscript] = useState(false);
    const [sharing, setSharing] = useState(false);

    useEffect(() => {
        api.get(`/sessions/${sessionId}`)
            .then(({ data }) => setSession(data))
            .catch(() => {
                toast.error("Couldn't load this session");
                navigate("/history");
            });
    }, [sessionId, navigate]);

    useEffect(() => {
        if (!session?.prompt_id) return;
        api.get(`/goals/${session.prompt_id}`).then(({ data }) => setGoal(data)).catch(() => {});
    }, [session]);

    if (!session) {
        return <div className="min-h-[60vh] flex items-center justify-center text-stone-500 italic font-serif text-2xl">Loading your reps…</div>;
    }

    const a = session.analysis || {};
    const m = session.metrics || {};
    const sc = a.scores || {};

    const copyIdeal = () => {
        navigator.clipboard.writeText(a.ideal_answer || "");
        toast.success("Ideal answer copied");
    };

    const toggleShare = async (next) => {
        setSharing(true);
        try {
            const { data } = await api.post(`/sessions/${session.id}/share`, { is_public: next });
            setSession({ ...session, is_public: data.is_public });
            if (next && data.public_url) {
                await navigator.clipboard.writeText(data.public_url).catch(() => {});
                toast.success("Public link copied to clipboard");
            } else {
                toast.success("Sharing turned off");
            }
        } catch {
            toast.error("Couldn't update sharing");
        } finally {
            setSharing(false);
        }
    };

    const copyShareLink = async () => {
        const url = `${window.location.origin}/s/${session.id}`;
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
    };

    return (
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-6 pb-8" data-testid="results-page">
            <Link to="/home" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-terracotta transition-colors">
                <ChevronLeft size={16} strokeWidth={1.5} /> Home
            </Link>

            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="mt-4"
            >
                <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-stone-400">{session.prompt_category}</div>
                <h1 className="font-serif text-3xl sm:text-5xl tracking-tight font-light mt-2">{session.prompt_title}</h1>
            </motion.div>

            {/* Score + key metrics */}
            <div className="mt-8 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-7 sm:p-8 grid sm:grid-cols-[auto_1fr] gap-8 items-center" data-testid="results-summary">
                <ScoreRing value={a.overall_score || 0} />
                <div>
                    <p className="font-serif text-2xl sm:text-3xl font-light leading-snug italic">
                        “{a.one_line_takeaway || "Keep going."}”
                    </p>
                    <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                        <div data-testid="metric-wpm">
                            <div className={`font-serif text-3xl font-light ${goal ? goalTone(compareGoal(m.wpm, goal, "wpm")) : ""}`}>{m.wpm || "—"}</div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">WPM</div>
                            {goal && m.wpm != null && (
                                <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-500">
                                    <span className={`w-1.5 h-1.5 rounded-full ${goalDot(compareGoal(m.wpm, goal, "wpm"))}`} />
                                    {goalLabel(compareGoal(m.wpm, goal, "wpm"))}
                                </div>
                            )}
                        </div>
                        <div data-testid="metric-fillers">
                            <div className={`font-serif text-3xl font-light ${goal && m.fillers_per_min != null ? goalTone(compareGoal(m.fillers_per_min, goal, "fillers_per_min")) : ""}`}>
                                {m.filler_count ?? "—"}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">Fillers</div>
                            {goal && m.fillers_per_min != null && (
                                <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-500">
                                    <span className={`w-1.5 h-1.5 rounded-full ${goalDot(compareGoal(m.fillers_per_min, goal, "fillers_per_min"))}`} />
                                    {goalLabel(compareGoal(m.fillers_per_min, goal, "fillers_per_min"))}
                                </div>
                            )}
                        </div>
                        <div data-testid="metric-duration">
                            <div className={`font-serif text-3xl font-light ${goal ? goalTone(compareGoal(m.duration_sec || 0, goal, "duration")) : ""}`}>
                                {Math.floor((m.duration_sec || 0) / 60)}:{((m.duration_sec || 0) % 60).toString().padStart(2, "0")}
                            </div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400 mt-1">Length</div>
                            {goal && (
                                <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-stone-500">
                                    <span className={`w-1.5 h-1.5 rounded-full ${goalDot(compareGoal(m.duration_sec || 0, goal, "duration"))}`} />
                                    {goalLabel(compareGoal(m.duration_sec || 0, goal, "duration"))}
                                </div>
                            )}
                        </div>
                    </div>
                    {goal && (
                        <div className="mt-4 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400" data-testid="goal-summary">
                            <Target size={12} strokeWidth={1.5} />
                            Goal: {goal.target_wpm_min}–{goal.target_wpm_max} WPM · ≤ {Number(goal.max_fillers_per_min)} fillers/min · {Math.floor(goal.target_duration_sec / 60)}:{(goal.target_duration_sec % 60).toString().padStart(2, "0")}
                        </div>
                    )}
                </div>
            </div>

            {/* Audio playback */}
            {session.audio_path && (
                <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-5" data-testid="results-audio-block">
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400 mb-3">
                        <Volume2 size={12} strokeWidth={1.5} /> Listen back
                    </div>
                    <AudioPlayer sessionId={session.id} />
                </div>
            )}

            {/* Share */}
            <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="share-block">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${session.is_public ? "bg-sage/15 text-sage" : "bg-stone-100 dark:bg-stone-800 text-stone-500"}`}>
                            {session.is_public ? <Globe size={18} strokeWidth={1.5} /> : <Lock size={18} strokeWidth={1.5} />}
                        </div>
                        <div>
                            <div className="font-medium text-sm">{session.is_public ? "This session is shareable" : "Share this scorecard"}</div>
                            <div className="text-xs text-stone-500">{session.is_public ? "Anyone with the link can view your scorecard." : "Turn on to get a public read-only link."}</div>
                        </div>
                    </div>
                    <Switch
                        checked={!!session.is_public}
                        onCheckedChange={toggleShare}
                        disabled={sharing}
                        data-testid="share-toggle"
                    />
                </div>
                {session.is_public && (
                    <div className="mt-4 flex items-center gap-2">
                        <code className="flex-1 text-xs sm:text-sm bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-full px-4 py-2 truncate" data-testid="share-link">
                            {window.location.origin}/s/{session.id}
                        </code>
                        <button onClick={copyShareLink} className="rounded-full p-2 hover:bg-stone-100 dark:hover:bg-stone-800" data-testid="copy-share-link">
                            <Share2 size={16} strokeWidth={1.5} />
                        </button>
                    </div>
                )}
            </div>

            {/* Subscores */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="results-subscores">
                {[
                    { key: "clarity", label: "Clarity", note: "How easy to follow" },
                    { key: "structure", label: "Structure", note: "How well it's built" },
                    { key: "content_relevance", label: "Content", note: "Did you answer it" },
                    { key: "delivery", label: "Delivery", note: "Pace + fillers + energy" },
                ].map((s) => {
                    const v = sc[s.key] || 0;
                    return (
                        <div key={s.key} className="rounded-2xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-5" data-testid={`subscore-${s.key}`}>
                            <div className="flex items-baseline justify-between">
                                <div>
                                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-400">{s.label}</div>
                                    <div className="text-xs text-stone-500 mt-0.5">{s.note}</div>
                                </div>
                                <div className="font-serif text-3xl font-light">{v}</div>
                            </div>
                            <div className="mt-3 h-1.5 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${subscoreColor(v)}`} style={{ width: `${v}%`, transition: "width 700ms cubic-bezier(0.16,1,0.3,1)" }} />
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Strengths / Weaknesses */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
                <div className="rounded-3xl border border-sage/20 bg-sage/5 p-6" data-testid="strengths-block">
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-sage">
                        <Sparkles size={12} strokeWidth={1.5} /> What worked
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                        {(a.strengths || []).length === 0 && <li className="italic text-stone-500">—</li>}
                        {(a.strengths || []).map((s, i) => (
                            <li key={i} className="flex gap-2"><span className="text-sage mt-1.5">•</span>{s}</li>
                        ))}
                    </ul>
                </div>
                <div className="rounded-3xl border border-terracotta/30 bg-terracotta/5 p-6" data-testid="weaknesses-block">
                    <div className="flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-bold text-terracotta">
                        <ScanText size={12} strokeWidth={1.5} /> What to sharpen
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
                        {(a.weaknesses || []).length === 0 && <li className="italic text-stone-500">—</li>}
                        {(a.weaknesses || []).map((s, i) => (
                            <li key={i} className="flex gap-2"><span className="text-terracotta mt-1.5">•</span>{s}</li>
                        ))}
                    </ul>
                </div>
            </div>

            {(a.structure_feedback || a.delivery_feedback) && (
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {a.structure_feedback && (
                        <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="structure-feedback">
                            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">On structure</div>
                            <p className="mt-2 text-sm leading-relaxed">{a.structure_feedback}</p>
                        </div>
                    )}
                    {a.delivery_feedback && (
                        <div className="rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="delivery-feedback">
                            <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">On delivery</div>
                            <p className="mt-2 text-sm leading-relaxed">{a.delivery_feedback}</p>
                            {Object.keys(m.filler_breakdown || {}).length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                    {Object.entries(m.filler_breakdown).map(([w, n]) => (
                                        <span key={w} className="px-2.5 py-0.5 rounded-full text-xs bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-300 border border-stone-200 dark:border-stone-700">
                                            "{w}" × {n}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {a.ideal_answer && (
                <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 backdrop-blur-md p-6 sm:p-7" data-testid="ideal-answer-block">
                    <div className="flex items-center justify-between">
                        <div className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">An ideal answer</div>
                        <button onClick={copyIdeal} className="text-xs text-stone-500 hover:text-terracotta inline-flex items-center gap-1.5" data-testid="copy-ideal-btn">
                            <Copy size={12} strokeWidth={1.5} /> Copy
                        </button>
                    </div>
                    <p className="font-serif text-lg sm:text-xl leading-relaxed mt-3 italic text-stone-800 dark:text-stone-200 whitespace-pre-wrap">
                        {a.ideal_answer}
                    </p>
                </div>
            )}

            <div className="mt-6 rounded-3xl border border-stone-200/80 dark:border-stone-800 bg-white/70 dark:bg-[#242322]/70 p-6" data-testid="transcript-block">
                <button
                    onClick={() => setShowTranscript((v) => !v)}
                    className="w-full flex items-center justify-between"
                    data-testid="transcript-toggle"
                >
                    <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-stone-400">Your answer · transcript</span>
                    {showTranscript ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showTranscript && (
                    <p className="mt-4 text-sm leading-relaxed text-stone-700 dark:text-stone-300 whitespace-pre-wrap" data-testid="transcript-text">
                        {session.transcript}
                    </p>
                )}
            </div>

            <div className="mt-8 flex gap-3 flex-wrap">
                <Link
                    to={`/practice/${session.prompt_id}`}
                    className="inline-flex items-center gap-2 rounded-full px-6 h-12 bg-terracotta hover:bg-[#D05A40] text-white font-medium"
                    data-testid="results-redo"
                >
                    <RotateCw size={16} /> Run it again
                </Link>
                <Link
                    to="/home"
                    className="inline-flex items-center gap-2 rounded-full px-6 h-12 border border-stone-300 dark:border-stone-700 hover:border-stone-500 font-medium"
                    data-testid="results-new"
                >
                    Try a new prompt <ArrowRight size={16} />
                </Link>
            </div>
        </div>
    );
}
